import type { RoleKey } from '@/api';

export interface RailItem {
  id: string;
  label: string;
  icon: string;
  to: string;
  /** Show the critical inbox count badge */
  badge?: 'inbox-critical' | 'tasks-critical';
}

/** Rails and landings per guideline 60-role-workspaces. */
export const WORKSPACES: Record<RoleKey, { landing: string; rail: RailItem[]; purpose: 'TREATMENT' | 'PAYMENT' | 'OPERATIONS' | 'COMPLIANCE' | 'PATIENT_REQUEST' }> = {
  provider: {
    landing: '/today',
    purpose: 'TREATMENT',
    rail: [
      { id: 'today', label: 'Today', icon: 'home', to: '/today' },
      { id: 'schedule', label: 'Schedule', icon: 'calendar', to: '/schedule' },
      { id: 'inbox', label: 'Inbox', icon: 'inbox', to: '/inbox', badge: 'inbox-critical' },
      { id: 'patients', label: 'Patients', icon: 'users', to: '/patients' },
      { id: 'worklists', label: 'Worklists', icon: 'orders', to: '/worklists' },
      { id: 'reports', label: 'Reports', icon: 'trend', to: '/reports' },
    ],
  },
  nurse: {
    landing: '/worklist',
    purpose: 'TREATMENT',
    rail: [
      { id: 'worklist', label: 'Worklist', icon: 'orders', to: '/worklist' },
      { id: 'patients', label: 'Patients', icon: 'users', to: '/patients' },
      { id: 'tasks', label: 'Tasks', icon: 'check', to: '/tasks', badge: 'tasks-critical' },
      { id: 'handoff', label: 'Handoff', icon: 'handoff', to: '/handoff' },
      { id: 'messages', label: 'Messages', icon: 'message', to: '/messages' },
    ],
  },
  front_desk: {
    landing: '/schedule',
    purpose: 'OPERATIONS',
    rail: [
      { id: 'schedule', label: 'Schedule', icon: 'calendar', to: '/schedule' },
      { id: 'arrivals', label: 'Arrivals', icon: 'bed', to: '/arrivals' },
      { id: 'patients', label: 'Patients', icon: 'users', to: '/patients' },
      { id: 'tasks', label: 'Tasks', icon: 'check', to: '/tasks' },
    ],
  },
  revenue: {
    landing: '/revenue',
    purpose: 'PAYMENT',
    rail: [
      { id: 'revenue', label: 'Queues', icon: 'inbox', to: '/revenue' },
      { id: 'accounts', label: 'Accounts', icon: 'billing', to: '/revenue/accounts' },
      { id: 'claims', label: 'Claims', icon: 'layers', to: '/revenue/claims' },
      { id: 'records', label: 'Records', icon: 'folder', to: '/revenue/records' },
      { id: 'reports', label: 'Reports', icon: 'trend', to: '/revenue/reports' },
    ],
  },
  privacy: {
    landing: '/compliance/audit',
    purpose: 'COMPLIANCE',
    rail: [
      { id: 'audit', label: 'Audit', icon: 'eye', to: '/compliance/audit' },
      { id: 'break-glass', label: 'Break-glass', icon: 'glass', to: '/compliance/break-glass' },
      { id: 'disclosures', label: 'Disclosures', icon: 'external', to: '/compliance/disclosures' },
      { id: 'holds', label: 'Holds', icon: 'lock', to: '/compliance/holds' },
      { id: 'exports', label: 'Exports', icon: 'folder', to: '/compliance/exports' },
      { id: 'consents', label: 'Consents', icon: 'shield', to: '/compliance/consents' },
      { id: 'approvals', label: 'Approvals', icon: 'key', to: '/compliance/approvals' },
      { id: 'report', label: 'Report', icon: 'trend', to: '/compliance/report' },
    ],
  },
  tenant_admin: {
    landing: '/admin',
    purpose: 'OPERATIONS',
    rail: [
      { id: 'admin', label: 'Admin', icon: 'sliders', to: '/admin' },
      { id: 'config', label: 'Changes', icon: 'merge', to: '/admin/config' },
      { id: 'ai', label: 'AI & CDS', icon: 'spark', to: '/admin/ai' },
      { id: 'ops', label: 'Operations', icon: 'trend', to: '/admin/ops' },
    ],
  },
  platform_operator: {
    landing: '/platform/tenants',
    purpose: 'OPERATIONS',
    rail: [{ id: 'tenants', label: 'Tenants', icon: 'building', to: '/platform/tenants' }],
  },
  result_interface: {
    landing: '/results-interface',
    purpose: 'OPERATIONS',
    rail: [{ id: 'unmatched', label: 'Unmatched', icon: 'flask', to: '/results-interface' }],
  },
  ai_governance: {
    landing: '/ai-signoff',
    purpose: 'OPERATIONS',
    rail: [{ id: 'ai-signoff', label: 'AI sign-off', icon: 'spark', to: '/ai-signoff' }],
  },
  patient: { landing: '/portal', purpose: 'PATIENT_REQUEST', rail: [] },
  proxy: { landing: '/portal', purpose: 'PATIENT_REQUEST', rail: [] },
};

/** Backend roles sharing visual workspaces still have distinct permissions. */
export function workspaceLanding(roleId: string, key: RoleKey): string {
  return roleId === 'him' ? '/identity-review' : WORKSPACES[key]?.landing ?? '/';
}

/** Use the actual role for both the rail and command palette, never its visual alias alone. */
export function workspaceRail(role: RoleKey, roleId: string, permissions: readonly string[]): RailItem[] {
  const base: RailItem[] = roleId === 'him'
    ? [{ id: 'patients', label: 'Patients', icon: 'users', to: '/patients' }, ...WORKSPACES.revenue.rail.filter(item => item.id === 'records')]
    : WORKSPACES[role].rail;
  return [...base.filter(item => item.to !== '/messages' || permissions.includes('message.read')),
    ...(permissions.includes('patient.duplicate.disposition') ? [{ id: 'identity', label: 'Identity review', icon: 'users', to: '/identity-review' }] : []),
    ...(permissions.includes('data_quality.manage') ? [{ id: 'data-quality', label: 'Data quality', icon: 'shield', to: '/data-quality' }] : []),
    ...(roleId === 'him' && permissions.includes('consent.read') ? [{ id: 'consents', label: 'Consents', icon: 'shield', to: '/compliance/consents' }] : []),
  ];
}

/** Where opening a patient takes each role (front desk never lands in the clinical chart). */
export function patientHome(role: RoleKey, patientId: string, roleId?: string): string | null {
  if (roleId === 'him') return `/registration/${patientId}`;
  switch (role) {
    case 'provider':
    case 'nurse':
      return `/chart/${patientId}`;
    case 'front_desk':
      return `/registration/${patientId}`;
    case 'revenue':
      return `/revenue/accounts/${patientId}`;
    case 'privacy':
      return `/compliance/consents/${patientId}`;
    default:
      return null;
  }
}
